import { Alert, Button, Card, InputNumber, Modal, Select, Space, Table, Typography, message } from "antd";
import { useMemo, useState } from "react";
import DistributorKycPanel from "./DistributorKycPanel";
import {
  DistributorInventoryPreviewItem,
  DistributorInventoryPreview,
  useAssignDistributorInventoryMutation,
  useGetDistributorInventorySummaryQuery,
  useGetDistributorsQuery,
  useLazyGetDistributorInventoryPreviewQuery,
} from "../../../api/admin/distributors/distributorsApi";

export default function DistributorInventoryPage() {
  const [distributorId, setDistributorId] = useState<string>();
  const [vendorSku, setVendorSku] = useState<string>();
  const [quantity, setQuantity] = useState<number>(1);
  const [preview, setPreview] = useState<DistributorInventoryPreview>();
  const [messageApi, contextHolder] = message.useMessage();
  const { data: distributors = [], isLoading: loadingDistributors } = useGetDistributorsQuery();
  const {
    data: summary,
    isLoading: loadingSummary,
    error: summaryError,
    refetch: refetchSummary,
  } = useGetDistributorInventorySummaryQuery();
  const [loadPreview, previewState] = useLazyGetDistributorInventoryPreviewQuery();
  const [assignInventory, assignState] = useAssignDistributorInventoryMutation();

  const activeDistributors = useMemo(
    () => distributors.filter((distributor) => distributor.status === "ACTIVE"),
    [distributors],
  );
  const hasExactPreview = preview?.preview.length === quantity;

  const clearPreview = () => setPreview(undefined);

  const onPreview = async () => {
    if (!vendorSku || quantity < 1 || quantity > 100) return;
    clearPreview();
    try {
      const result = await loadPreview({ vendorSku, quantity }).unwrap();
      setPreview(result);
    } catch {
      messageApi.error("Unable to preview available inventory");
    }
  };

  const onAssign = () => {
    if (!distributorId || !vendorSku || !preview || !hasExactPreview) return;
    const distributor = activeDistributors.find((item) => item.id === distributorId);
    Modal.confirm({
      title: "Assign distributor stock",
      content: `Assign ${quantity} ${vendorSku} eSIM${quantity === 1 ? "" : "s"} to ${distributor?.name || distributor?.code || distributorId}?`,
      okText: "Assign stock",
      onOk: async () => {
        try {
          const result = await assignInventory({
            distributorId,
            vendorSku,
            iccids: preview.preview.map((item) => item.iccid),
          }).unwrap();
          messageApi.success(`Assigned ${result.assignedCount} eSIMs to ${result.distributorCode}`);
          clearPreview();
          await refetchSummary();
        } catch {
          messageApi.error("Inventory changed after preview. Refresh and try again.");
        }
      },
    });
  };

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      {contextHolder}
      <div>
        <Typography.Title level={3} style={{ marginBottom: 4 }}>Distributor eSIM Stock</Typography.Title>
        <Typography.Text type="secondary">
          Preview available generic eSIMs and reserve them for a distributor.
        </Typography.Text>
      </div>
      {summaryError && <Alert type="error" showIcon message="Unable to load available inventory" />}
      <Card>
        <Space wrap align="end">
          <Space direction="vertical" size={4}>
            <Typography.Text>Distributor</Typography.Text>
            <Select
              showSearch
              optionFilterProp="label"
              style={{ minWidth: 260 }}
              loading={loadingDistributors}
              value={distributorId}
              placeholder="Select an active distributor"
              options={activeDistributors.map((distributor) => ({
                value: distributor.id,
                label: `${distributor.code} — ${distributor.name}`,
              }))}
              onChange={(value) => {
                setDistributorId(value);
                clearPreview();
              }}
            />
          </Space>
          <Space direction="vertical" size={4}>
            <Typography.Text>Provider SKU</Typography.Text>
            <Select
              showSearch
              optionFilterProp="label"
              style={{ minWidth: 280 }}
              loading={loadingSummary}
              value={vendorSku}
              placeholder="Select available stock"
              options={(summary?.skus || []).map((item) => ({
                value: item.vendorSku,
                label: `${item.vendorSku} (${item.availableCount} available)`,
              }))}
              onChange={(value) => {
                setVendorSku(value);
                clearPreview();
              }}
            />
          </Space>
          <Space direction="vertical" size={4}>
            <Typography.Text>Quantity</Typography.Text>
            <InputNumber
              min={1}
              max={100}
              value={quantity}
              onChange={(value) => {
                setQuantity(value || 1);
                clearPreview();
              }}
            />
          </Space>
          <Button
            onClick={onPreview}
            loading={previewState.isFetching}
            disabled={!vendorSku || quantity < 1 || quantity > 100}
          >
            Preview stock
          </Button>
          <Button
            type="primary"
            onClick={onAssign}
            loading={assignState.isLoading}
            disabled={!distributorId || !hasExactPreview || previewState.isFetching}
          >
            Assign stock
          </Button>
        </Space>
      </Card>
      {preview && preview.preview.length < quantity && (
        <Alert
          type="warning"
          showIcon
          message={`Only ${preview.availableCount} eligible eSIMs are available for ${preview.vendorSku}.`}
        />
      )}
      <Card title={preview ? `Selected inventory (${preview.preview.length})` : "Selected inventory"}>
        <Table<DistributorInventoryPreviewItem>
          rowKey="iccid"
          loading={previewState.isFetching}
          dataSource={preview?.preview || []}
          scroll={{ y: 420 }}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          columns={[
            { title: "ICCID", dataIndex: "iccid" },
            { title: "Provider SKU", dataIndex: "vendorSku" },
            { title: "Expiry date", dataIndex: "expiryDate", render: (value) => value || "—" },
            { title: "Uploaded", dataIndex: "createdAt", render: (value) => value || "—" },
          ]}
        />
      </Card>
      {distributorId && (
        <DistributorKycPanel
          key={distributorId}
          distributorId={distributorId}
        />
      )}
    </Space>
  );
}
